import { Button } from "@package/subframe/components/Button";
import {
  FeatherCalendar,
  FeatherClipboardCheck,
  FeatherClock,
} from "@subframe/core";
import { Link } from "@tanstack/react-router";

import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { TintedEntityIcon } from "@/components/ui/entity-icon";
import type { ScheduledQuestionnaireListItemWithSync } from "@/hooks/use-offline-scheduled-questionnaires";
import {
  getButtonLabel,
  getStatusBadges,
  getTimeDisplayInfo,
  isCompletedItem,
  isFutureItem,
} from "@/lib/checklist-utils";
import { checklistStatusPill } from "@/lib/workspace/status-pill";

interface ChecklistRowProps {
  item: ScheduledQuestionnaireListItemWithSync;
  isOffline: boolean;
}

export function ChecklistRow({ item, isOffline }: ChecklistRowProps) {
  const isFuture = isFutureItem(item);
  const isCompleted = isCompletedItem(item);
  const timeInfo = getTimeDisplayInfo(item);
  const { issuesBadge, syncBadge } = getStatusBadges(item, { isOffline });
  const buttonLabel = getButtonLabel(item);

  const categories = item.config?.categories ?? [];
  const primaryCategory =
    categories.find((c) => c.id === item.config?.primaryCategoryId) ??
    categories[0];

  const content = (
    <div className="flex w-full items-center justify-between gap-4 rounded-sm px-4 py-3 mobile:items-start mobile:gap-3 mobile:rounded-none mobile:px-4 mobile:py-4">
      <div className="flex items-center gap-3 grow shrink-0 basis-0">
        <TintedEntityIcon
          entity="checklist"
          slug={primaryCategory?.slug}
          color={primaryCategory?.color}
          size={40}
          fallbackIcon={<FeatherClipboardCheck />}
          title={
            primaryCategory ? `Checklist — ${primaryCategory.name}` : undefined
          }
        />
        <div className="flex flex-col items-start gap-1">
          <span
            className={`text-body-bold font-body-bold text-neutral-900 mobile:text-large-button mobile:font-large-button ${isCompleted ? "line-through" : ""}`}
          >
            {item.template.name}
          </span>
          <div className="flex items-center gap-1 mobile:gap-1.5">
            {timeInfo.useCalendarIcon ? (
              <FeatherCalendar
                className={`text-caption font-caption mobile:text-large-button mobile:font-large-button ${timeInfo.isError ? "text-error-700" : "text-subtext-color"}`}
              />
            ) : (
              <FeatherClock className="text-caption font-caption text-subtext-color mobile:text-large-button mobile:font-large-button" />
            )}
            <span
              className={`text-caption font-caption mobile:text-body mobile:font-body ${timeInfo.isError ? "text-error-700" : "text-subtext-color"}`}
            >
              {timeInfo.text}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 flex-shrink-0 mobile:flex-col mobile:items-end mobile:gap-2">
        {issuesBadge}
        {syncBadge}
        <WorkspaceStatusBadge pill={checklistStatusPill(item)} />
        <Button className="mobile:hidden" variant="neutral-primary">
          {buttonLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <Link
      to="/checklists/$id"
      params={{ id: String(item.id) }}
      search={isFuture ? { mode: "preview" } : undefined}
      className="block hover:bg-neutral-50 transition-colors"
    >
      {content}
    </Link>
  );
}
